import {useMutation} from '@apollo/client';
import {IClubForm} from 'packages/common-types/base-types';
import {useCallback, useState} from 'react';
import {
  CreateClubData,
  CreateClubVars,
  UpdateClubData,
  UpdateClubVars,
} from 'packages/common-types/mutation-types';
import {CREATE_CLUB, UPDATE_CLUB} from 'packages/graphql/v2/mutations';
import Swal from 'sweetalert2';
import {GET_CLUBS} from 'packages/graphql/v2/queries';

type UseClubValues = {
  loading: boolean;
  onSubmit: (formData: IClubForm) => Promise<void>;
  onUpdateSubmit: (formData: IClubForm) => Promise<void>;
};

const useClub = (clubId: string): UseClubValues => {
  const [loading, setLoading] = useState(false);
  const [createClub] = useMutation<CreateClubData, CreateClubVars>(CREATE_CLUB);
  const [updateClub] = useMutation<UpdateClubData, UpdateClubVars>(UPDATE_CLUB);

  const onSubmit = useCallback(
    async (formData: IClubForm) => {
      const {country, state, city, name} = formData;
      setLoading(true);
      try {
        const input = {name, address: {country, state, city}};
        await createClub({
          variables: {
            input,
          },
          refetchQueries: [
            {
              query: GET_CLUBS,
            },
          ],
        });
        Swal.fire({
          title: 'Success',
          text: 'El club fue creado correctamente',
          icon: 'success',
        });
      } catch (error) {
        console.error('ERROR => ', error);
        Swal.fire({
          title: 'Error',
          text: 'Ocurrio un error al crear el club',
          icon: 'error',
        });
      } finally {
        setLoading(false);
      }
    },
    [createClub]
  );

  const onUpdateSubmit = useCallback(
    async (formData: IClubForm) => {
      const {country, state, city, name} = formData;
      setLoading(true);
      try {
        const input = {name, address: {country, state, city}};
        await updateClub({
          variables: {
            clubId,
            clubModify: input,
          },
          refetchQueries: [
            {
              query: GET_CLUBS,
            },
          ],
        });
        Swal.fire({
          title: 'Success',
          text: 'El club fue actualizado correctamente',
          icon: 'success',
        });
      } catch (error) {
        console.error('ERROR => ', error);
        Swal.fire({
          title: 'Error',
          text: 'Ocurrio un error al actualizar el club',
          icon: 'error',
        });
      } finally {
        setLoading(false);
      }
    },
    [updateClub, clubId]
  );
  return {loading, onSubmit, onUpdateSubmit};
};

export default useClub;
